import { Component, JSX, JSXElement, Show } from 'solid-js';
import { createStore } from 'solid-js/store';
import { twclsx } from './twclsx';
import { type ZodSchema } from 'zod';
import { type Validator } from '../globals';

type Props = {
  label?: string | JSXElement;
  type: string;
  id: string;
  name: string;
  class?: string;
  inputClass?: string;
  inputDivClass?: string;
  labelClass?: string;
  step?: string;
  placeholder: string;
  value?: string | number | undefined;
  onChange?: (e: Event & { currentTarget: HTMLInputElement; target: HTMLInputElement }) => void;
  onInput?: (e: Event & { currentTarget: HTMLInputElement; target: HTMLInputElement }) => void;
  onKeyDown?: JSX.EventHandlerUnion<HTMLInputElement, KeyboardEvent>;
  onBlur?: JSX.EventHandlerUnion<HTMLInputElement, FocusEvent>;
  validators?: Validator[];
  required?: boolean;
  disabled?: boolean;
  rightLabel?: JSXElement;
  autocomplete?: string;
  min?: number;
  pattern?: string;
};

const [errors, setErrors] = createStore<{ [key: string]: string }>({});
const errorClasses = {
  error: {
    label: twclsx('text-red-700', 'dark:text-red-500'),
    input: twclsx(
      'bg-red-50',
      'border-red-500',
      'text-red-900',
      'placeholder-red-700',
      'focus:ring-red-500',
      'dark:bg-gray-700',
      'focus:border-red-500',
      'dark:text-red-500',
      'dark:placeholder-red-500',
      'dark:border-red-500',
    ),
  },
  base: {
    label: twclsx('text-gray-900', 'dark:text-white'),
    input: twclsx(
      'bg-gray-50',
      'border-gray-300',
      'text-gray-900',
      'focus:ring-primary-500',
      'focus:border-primary-500',
      'dark:bg-gray-700',
      'dark:border-gray-600',
      'dark:placeholder-gray-400',
      'dark:text-white',
      'dark:focus:ring-primary-500',
      'dark:focus:border-primary-500',
    ),
  },
};

function checkValid(
  { element, validators = [] }: { element: HTMLInputElement; validators: Validator[] },
  setErrors: (errors: Record<string, string | undefined>) => void,
) {
  return async () => {
    element.setCustomValidity('');
    element.checkValidity();
    const text = [];
    // TODO: make sure this is applicable in all cases
    if (element.value === '') {
      return;
    }

    for (const validator of validators) {
      if (typeof validator === 'function') {
        text.push(validator(element.value));
      } else {
        const validate = validator.safeParse(element.value);
        if (!validate.success) {
          text.push(validate.error.issues.map(({ message }) => message).join(' '));
        }
      }
    }

    const message = text.join(', ');

    element.setCustomValidity(message);
    setErrors({ [element.name]: message || undefined });
  };
}

const validate = (ref: HTMLInputElement, accessor: () => ZodSchema) => {
  const accessorValue = accessor();
  const validators = Array.isArray(accessorValue) ? accessorValue : [];

  ref.onblur = checkValid({ element: ref, validators }, setErrors);

  ref.oninput = () => {
    if (!errors[ref.name]) {
      return;
    }

    setErrors({ [ref.name]: undefined });
  };
};

const Input: Component<Props> = props => (
  <div class={twclsx(props.class)}>
    <Show when={props.label}>
      <label
        for={props.id}
        class={twclsx(
          'block',
          'text-sm',
          'font-medium',
          'text-gray-600',
          'dark:text-gray-400',
          props.labelClass,
          errors[props.name] ? errorClasses.error.label : errorClasses.base.label,
        )}
      >
        {props.label}
      </label>
    </Show>
    <div class={twclsx('relative', props.inputDivClass)}>
      <input
        {...props}
        type={props.type}
        id={props.id}
        name={props.name}
        placeholder={props.placeholder}
        autocomplete={props.autocomplete}
        value={props.value}
        step={props.step}
        onChange={(...args) => props.onChange?.(...args)}
        onInput={(...args) => props.onInput?.(...args)}
        onKeyDown={props.onKeyDown}
        onBlur={props.onBlur}
        required={props.required || false}
        disabled={props.disabled}
        min={props.type === 'number' ? props.min : undefined}
        pattern={props.pattern}
        use:validate={props.validators}
        class={twclsx(
          'block',
          'w-full',
          'p-2.5',
          'border',
          'text-sm',
          'rounded-lg',
          'placeholder:text-left',
          errors[props.name] ? errorClasses.error.input : errorClasses.base.input,
          props.inputClass,
        )}
      />
      <Show when={props.rightLabel}>
        <span
          class={twclsx('absolute', 'inset-y-0', 'right-0', 'flex', 'items-center', 'pe-3.5', 'pointer-events-none')}
        >
          <span class={twclsx('flex', 'items-center')}>{props.rightLabel}</span>
        </span>
      </Show>
    </div>

    <Show when={errors[props.name]}>
      <p class={twclsx('mt-2', 'text-sm', 'text-red-600', 'dark:text-red-500')}>{errors[props.name]}</p>
    </Show>
  </div>
);

export default Input;
